import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Keeps scroll position sane across client-side navigation.
 *
 *  - a new path with no hash starts at the top
 *  - a path with a hash scrolls to that section
 *
 * The hash case matters on a single-page site: the header links are anchors,
 * and following one from `/privacy` has to land on the right section rather
 * than at the top of the page.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      return;
    }

    // The target may not exist yet if the route is still loading its chunk.
    let frame = 0;
    let attempts = 0;

    const tryScroll = () => {
      const target = document.querySelector(hash);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (attempts < 20) {
        attempts += 1;
        frame = requestAnimationFrame(tryScroll);
      }
    };

    frame = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return null;
}
